import { ExternalLink } from "lucide-react";
import gridPattern from "../assets/abstract-grid-pattern.png";

import ChromeLogo from "../assets/icons/chrome-browser.svg";
import SafariLogo from "../assets/icons/safari-browser.svg";
import FirefoxLogo from "../assets/icons/firefox-new.png";
import EdgeLogo from "../assets/icons/edge-browser.svg";
import OperaLogo from "../assets/icons/opera-browser.svg";
import { useTranslation } from "../i18n";

export default function CompatibilitySection() {
  const { t } = useTranslation();

  const browsers = [
    {
      name: t("routes.webApp.components.showCase.compatibility.browsers.0"),
      icon: ChromeLogo,
    },
    {
      name: t("routes.webApp.components.showCase.compatibility.browsers.1"),
      icon: SafariLogo,
    },
    {
      name: t("routes.webApp.components.showCase.compatibility.browsers.2"),
      icon: FirefoxLogo,
    },
    {
      name: t("routes.webApp.components.showCase.compatibility.browsers.3"),
      icon: EdgeLogo,
    },
    {
      name: t("routes.webApp.components.showCase.compatibility.browsers.4"),
      icon: OperaLogo,
    },
  ];

  const devices = [
    t("routes.webApp.components.showCase.compatibility.devices.0"),
    t("routes.webApp.components.showCase.compatibility.devices.1"),
    t("routes.webApp.components.showCase.compatibility.devices.2"),
    t("routes.webApp.components.showCase.compatibility.devices.3"),
    t("routes.webApp.components.showCase.compatibility.devices.4"),
  ];

  return (
    <section className=" relative overflow-hidden">
      {/* Container with max width and rounded corners */}
      <div className="">
        <div className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-8 md:p-12 shadow-lg border border-blue-100 overflow-hidden w-full">
          {/* Background blur effects */}
          <div
            className="absolute top-0 left-0 w-full h-full bg-repeat opacity-10 z-0"
            style={{ backgroundImage: `url(${gridPattern})` }}
          ></div>
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
          <div className="absolute top-1/3 left-1/4 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-2xl opacity-20"></div>
          {/* Content */}
          <div className="relative z-10">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
                {t("routes.webApp.components.showCase.compatibility.badge")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text">
                {t("routes.webApp.components.showCase.compatibility.heading")}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t(
                  "routes.webApp.components.showCase.compatibility.description"
                )}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Browser Compatibility */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900">
                  {t(
                    "routes.webApp.components.showCase.compatibility.browserHeading"
                  )}
                </h3>
                <p className="text-gray-600 mb-8">
                  {t(
                    "routes.webApp.components.showCase.compatibility.browserDescription"
                  )}
                </p>

                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="grid grid-cols-5 gap-4">
                    {browsers.map((browser) => (
                      <div
                        key={browser.name}
                        className="flex flex-col items-center"
                      >
                        <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-2 p-2">
                          <img
                            src={browser.icon}
                            alt={browser.name}
                            className="w-8 h-8"
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          {browser.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Device Compatibility */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-gray-900">
                  {t(
                    "routes.webApp.components.showCase.compatibility.deviceHeading"
                  )}
                </h3>
                <p className="text-gray-600 mb-8">
                  {t(
                    "routes.webApp.components.showCase.compatibility.deviceDescription"
                  )}
                </p>

                <div className="bg-white rounded-xl shadow-md p-6">
                  <ul className="grid grid-cols-2 gap-4">
                    {devices.map((device) => (
                      <li key={device} className="flex items-center">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center mr-2">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="3"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                        </div>
                        <span className="text-gray-700">{device}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Launch Button */}
            <div className="mt-12 text-center">
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="https://app.juhuu.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                >
                  {t(
                    "routes.webApp.components.showCase.compatibility.openWebApp"
                  )}
                  <ExternalLink className="ml-2 h-5 w-5" />
                </a>
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg font-medium transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                >
                  {t(
                    "routes.webApp.components.showCase.compatibility.contactSales"
                  )}
                </a>
              </div>
              <p className="mt-4 text-gray-500">
                {t(
                  "routes.webApp.components.showCase.compatibility.noDownloads"
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
